import { useState, useEffect } from 'react';
import { setGridEnabled, getGridEnabled, performUndo, performRedo, canUndo, canRedo } from '../libs/BitmapEditor/bitmapMaker3';
import { useForceUpdate } from '../hooks/forceUpdate';
import styled from 'styled-components';
import { IconArrowBackUp, IconArrowForwardUp } from '@tabler/icons-react';
import { IconGrid4x4 } from '@tabler/icons-react';

export default function ToolsGrid() {
	const [gridEnabled, setGridEnabledState] = useState(false);
	const [undoAvailable, setUndoAvailable] = useState(false);
	const [redoAvailable, setRedoAvailable] = useState(false);
	const handleForceUpdate = useForceUpdate();

	// Initialize grid state from bitmapMaker3
	useEffect(() => {
		setGridEnabledState(getGridEnabled());
		updateHistoryState();
	}, []);

	// Update undo/redo button states
	const updateHistoryState = () => {
		const undoState = canUndo();
		const redoState = canRedo();
		setUndoAvailable(undoState);
		setRedoAvailable(redoState);
	};

	const toggleGrid = () => {
		const newGridState = !gridEnabled;
		setGridEnabledState(newGridState);
		setGridEnabled(newGridState);
		// Force canvas to re-render
		handleForceUpdate();
	};

	const handleUndo = () => {
		const result = performUndo();
		if (result) {
			updateHistoryState();
		}
	};

	const handleRedo = () => {
		const result = performRedo();
		if (result) {
			updateHistoryState();
		}
	};

	// Update history state periodically (for when user draws)
	useEffect(() => {
		const interval = setInterval(updateHistoryState, 500);
		return () => clearInterval(interval);
	}, []);

	return (
		<GridContainer>
			<HistoryButtons>
				<HistoryButton onClick={handleUndo} disabled={!undoAvailable} title="Undo">
					<IconArrowBackUp size={18} />
				</HistoryButton>
				<HistoryButton onClick={handleRedo} disabled={!redoAvailable} title="Redo">
					<IconArrowForwardUp size={18} />
				</HistoryButton>
			</HistoryButtons>

			<GridToggleButton onClick={toggleGrid} enabled={gridEnabled}>
				<IconGrid4x4 stroke={2} size={16} color={'white'} />
				{gridEnabled ? 'Hide Grid' : 'Show Grid'}
			</GridToggleButton>
		</GridContainer>
	);
}

const GridContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	gap: 10px;
`;

const HistoryButtons = styled.div`
	display: flex;
	gap: 5px;
`;

const HistoryButton = styled.button<{ disabled: boolean }>`
	padding: 8px;
	border-radius: 6px;
	border: none;
	background-color: ${props => (props.disabled ? '#333' : '#555')};
	color: ${props => (props.disabled ? '#666' : 'white')};
	cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
	transition: background-color 0.2s;
	display: flex;
	align-items: center;
	justify-content: center;

	&:hover {
		background-color: ${props => (props.disabled ? '#333' : '#777')};
	}

	&:active {
		transform: ${props => (props.disabled ? 'none' : 'scale(0.95)')};
	}
`;

const GridToggleButton = styled.button.withConfig({
	shouldForwardProp: prop => prop !== 'enabled',
})<{ enabled: boolean }>`
	padding: 8px 16px;
	border-radius: 8px;
	border: none;
	background-color: ${props => (props.enabled ? '#e5b800' : '#666')};
	color: white;
	font-size: 12px;
	font-weight: bold;
	cursor: pointer;
	transition: background-color 0.2s;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;

	&:hover {
		background-color: ${props => (props.enabled ? '#e5b800' : '#555')};
	}

	&:active {
		transform: scale(0.98);
	}
`;
